import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Loader2, RefreshCw } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';
import { useEnterpriseEmailMonitoring } from '@/hooks/useEnterpriseEmailMonitoring';
import { useEnterpriseMemberAnalytics } from '@/hooks/useEnterprisePartnerAnalytics';
import { useEnterpriseEmulationActivity } from '@/hooks/useEnterpriseEmulationActivity';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HealthStatusLine } from '@/components/enterprise/dashboard/HealthStatusLine';
import { AlertQueuePanel } from '@/components/enterprise/dashboard/AlertQueuePanel';
import { EmailAtAGlance } from '@/components/enterprise/dashboard/EmailAtAGlance';
import { MemberSeatSummary } from '@/components/enterprise/dashboard/MemberSeatSummary';
import { EmulationActivityList } from '@/components/enterprise/dashboard/EmulationActivityList';
import { MemberAnalyticsTable } from '@/components/enterprise/dashboard/MemberAnalyticsTable';
import {
  EnterpriseRecordDetailSheet,
  type EnterpriseDetailRecord,
} from '@/components/enterprise/EnterpriseRecordDetailSheet';

/**
 * /enterprise/dashboard — M3 (#573) cross-workspace triage + analytics dashboard.
 *
 * PAGE gate: visible to anyone holding ANY enterprise capability (email_monitor
 * OR analytics_read) on at least one partner; else redirect home. The gate is
 * cosmetic — every server RPC recomputes authorization per partner and filters
 * each row to that partner's workspaces.
 *
 * CARD (capability) composition: alert/email cards render only when the caller
 * has `email_monitor` for the selected partner; analytics/member/emulation cards
 * render only when they have `analytics_read`. Partner admins hold both. A
 * per-card 403 surfaces as that card's own error state (the cards drive their
 * own skeleton + error + retry off their own query — never a page-level collapse).
 */
export function EnterpriseDashboard(): JSX.Element {
  const { partners, isLoading: capsLoading, hasCapability } = useEnterpriseCapabilities();

  const dashboardPartners = useMemo(
    () =>
      partners.filter(
        (p) =>
          hasCapability(p.partner_id, 'analytics_read') ||
          hasCapability(p.partner_id, 'email_monitor'),
      ),
    [partners, hasCapability],
  );

  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const partnerId = selectedPartnerId ?? dashboardPartners[0]?.partner_id ?? '';

  // The record shown in the inline detail Sheet (null = closed). Admins aren't
  // members of the workspaces they monitor, so "View" opens this read-only
  // detail instead of switching workspace.
  const [detail, setDetail] = useState<EnterpriseDetailRecord | null>(null);

  // Per-card capability booleans for the selected partner.
  const canMonitor = hasCapability(partnerId, 'email_monitor');
  const canAnalytics = hasCapability(partnerId, 'analytics_read');

  // M2 email monitoring — three independent queries, exposed individually so
  // each alert/email card owns its skeleton + error + retry.
  const {
    campaignsQuery,
    sendersQuery,
    quotasQuery,
    isLoading: monitorLoading,
    error: monitorError,
    refetch: refetchMonitor,
  } = useEnterpriseEmailMonitoring(canMonitor ? partnerId : '');

  // M3 analytics + emulation — each its own query.
  const membersQuery = useEnterpriseMemberAnalytics(canAnalytics ? partnerId : '');
  const emulationQuery = useEnterpriseEmulationActivity(canAnalytics ? partnerId : '');

  const campaigns = campaignsQuery.data ?? [];
  const senders = sendersQuery.data ?? [];
  const quotas = quotasQuery.data ?? [];
  const members = membersQuery.data ?? [];
  const emulation = emulationQuery.data ?? [];

  // Surface each failing query to GlitchTip (per CLAUDE.md telemetry convention).
  useEffect(() => {
    const err =
      campaignsQuery.error ??
      sendersQuery.error ??
      quotasQuery.error ??
      membersQuery.error ??
      emulationQuery.error;
    if (err) {
      captureError(err as Error, { feature: 'enterprise-dashboard' });
    }
  }, [
    campaignsQuery.error,
    sendersQuery.error,
    quotasQuery.error,
    membersQuery.error,
    emulationQuery.error,
  ]);

  const refreshAll = () => {
    if (canMonitor) refetchMonitor();
    if (canAnalytics) {
      void membersQuery.refetch();
      void emulationQuery.refetch();
    }
  };

  const refreshing =
    (canMonitor && monitorLoading) ||
    (canAnalytics && (membersQuery.isFetching || emulationQuery.isFetching));

  // --- Gating (after hooks, to satisfy rules-of-hooks) ---------------------
  if (capsLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (dashboardPartners.length === 0) {
    return <Navigate to="/" replace />;
  }

  const selectedPartner =
    dashboardPartners.find((p) => p.partner_id === partnerId) ?? null;

  // Health line aggregates only the sources the caller can actually see.
  const healthLoading =
    (canMonitor && monitorLoading) || (canAnalytics && emulationQuery.isLoading);

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Building2 className="h-5 w-5 text-foreground" />
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            What needs attention across the workspaces in
            {selectedPartner ? ` ${selectedPartner.name}` : ' your enterprise'}, plus a
            cross-workspace adoption rollup.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {dashboardPartners.length > 1 && (
            <div className="w-56">
              <Select value={partnerId} onValueChange={(v) => setSelectedPartnerId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select enterprise" />
                </SelectTrigger>
                <SelectContent>
                  {dashboardPartners.map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={refreshAll} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <HealthStatusLine
        campaigns={canMonitor ? campaigns : []}
        senders={canMonitor ? senders : []}
        quotas={canMonitor ? quotas : []}
        emulation={canAnalytics ? emulation : []}
        isLoading={healthLoading}
      />

      {canMonitor && (
        <>
          <AlertQueuePanel
            campaigns={campaigns}
            senders={senders}
            quotas={quotas}
            emulation={canAnalytics ? emulation : []}
            isLoading={monitorLoading}
            error={(monitorError as Error | null) ?? null}
            onRetry={refetchMonitor}
            onSelect={setDetail}
          />
          <EmailAtAGlance
            campaigns={campaigns}
            isLoading={campaignsQuery.isLoading}
            error={(campaignsQuery.error as Error | null) ?? null}
          />
        </>
      )}

      {canAnalytics && (
        <>
          <MemberSeatSummary
            members={members}
            isLoading={membersQuery.isLoading}
            error={(membersQuery.error as Error | null) ?? null}
          />
          <EmulationActivityList
            emulation={emulation}
            isLoading={emulationQuery.isLoading}
            error={(emulationQuery.error as Error | null) ?? null}
            onRetry={() => {
              void emulationQuery.refetch();
            }}
          />
          <MemberAnalyticsTable
            members={members}
            isLoading={membersQuery.isLoading}
            error={(membersQuery.error as Error | null) ?? null}
            onRetry={() => {
              void membersQuery.refetch();
            }}
          />
        </>
      )}

      <EnterpriseRecordDetailSheet
        record={detail}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      />
    </div>
  );
}

export default EnterpriseDashboard;
